import { FEELING_COLORS3 } from '@/show/freeConfig';
import { ROOT_GLOSS, glossLine } from '@/show/saysConfig';
import { Transmission, MiniScope } from './StageHud';

const Row = ({ k, v, testId, warn }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-0.5">
    <span className="text-[var(--muted)]">{k}</span>
    <span data-testid={testId} className={warn ? 'text-[var(--amber)]' : 'text-[var(--ink)]'}>{v}</span>
  </div>
);

const LoggedTracker = ({ snap }) => (
  <div className="flex flex-col items-end gap-3">
    <div className="label-eyebrow">FEELINGS LOGGED <b data-testid="feelings-logged-count" className="text-[var(--ink)]">{snap.discovered.length}/6</b></div>
    <div className="flex gap-2">
      {snap.feelings.map((f) => {
        const found = snap.discovered.includes(f);
        return (
          <div key={f} data-testid={`logged-${f}`} data-found={found} className={`border px-3 py-1.5 text-xs tracking-[0.25em] ${found ? '' : 'glitch text-[var(--dim)]'}`} style={found ? { borderColor: FEELING_COLORS3[f], color: FEELING_COLORS3[f] } : { borderColor: 'var(--hair)' }}>
            {found ? f.toUpperCase() : '?????'}
          </div>
        );
      })}
    </div>
  </div>
);

const ExcitationMeter = ({ snap }) => {
  const color = FEELING_COLORS3[snap.prompted] || 'var(--spore)';
  return (
    <div className="stage-panel w-[380px] p-4">
      <div className="flex justify-between label-eyebrow"><span>EXCITATION INDEX</span><b data-testid="excitation-value" className="text-[var(--ink)]">{Math.round(snap.excitation)}%</b></div>
      <div className="mt-2 h-3 border border-white/10"><div data-testid="excitation-bar" className="h-full transition-[width] duration-200" style={{ width: `${snap.excitation}%`, background: color, boxShadow: `0 0 18px ${color}` }} /></div>
      <div className="mt-3 flex justify-between label-eyebrow"><span>LONGING</span><b data-testid="longing-value" className="text-[var(--ink)]">{Math.round(snap.longing)}%</b></div>
      <div className="mt-1 h-1.5 border border-white/10"><div className="h-full bg-[#7f9cf5] transition-[width] duration-200" style={{ width: `${snap.longing}%` }} /></div>
      <div data-testid="current-hypothesis" className="mt-4 text-sm tracking-[0.3em]">
        CURRENT HYPOTHESIS: <span style={{ color }} className="font-bold">{snap.prompted ? `${snap.prompted.toUpperCase()}?` : '—'}</span>
      </div>
      <div data-testid="dominant-style" className="mt-1 text-xs tracking-[0.2em] text-[var(--muted)]">STYLE READING: {snap.dominantStyle ? snap.dominantStyle.toUpperCase() : 'NONE'}</div>
    </div>
  );
};

export const FreeAudienceHud = ({ snap }) => (
  <div data-testid="audience-hud" className="pointer-events-none absolute inset-0 flex flex-col justify-between p-10">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-eyebrow">[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</div>
        <div className="font-display mt-1 text-2xl font-extrabold">SPORE Language Assessment - Feelings.</div>
        <div data-testid="stage-phase-label" className="label-eyebrow mt-2">{snap.phase === 'opening' ? 'INTRODUCTION' : snap.phase === 'play' ? 'FREE EXPLORATION' : snap.phase.toUpperCase()}</div>
      </div>
      <LoggedTracker snap={snap} />
    </div>
    <div className="flex items-end justify-between gap-6">
      <ExcitationMeter snap={snap} />
      <div className="max-w-[760px] flex-1"><Transmission tx={snap.ui.transmission} /></div>
      <MiniScope />
    </div>
  </div>
);

export const FreeRevealStamp = ({ snap }) => {
  const f = snap.discovered[snap.discovered.length - 1];
  const color = FEELING_COLORS3[f];
  return (
    <div data-testid="reveal-stamp" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-10">
      <div data-testid="guess-stamp" className="stamp px-10 py-4 text-center" style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
        <div className="text-xs tracking-[0.5em] text-[var(--muted)]">FIELD DICTIONARY · PR. H. CHAMPIGNON</div>
        <div className="font-display mt-1 text-4xl font-extrabold tracking-[0.1em]">FEELING LOGGED: <span data-testid="guess-value">{snap.guesses[f] || '…'}</span> (?)</div>
      </div>
      {snap.phaseMs > 1200 && (
        <div data-testid="analysis-stamp" className="stamp px-14 py-8 text-center" style={{ borderColor: color, color, boxShadow: `0 0 80px ${color}55, inset 0 0 40px ${color}22`, transform: 'rotate(2deg)' }}>
          <div className="text-sm tracking-[0.5em] text-[var(--ink)]">SPECTRAL ANALYSIS</div>
          <div data-testid="reveal-feeling" className="font-display mt-2 text-7xl font-extrabold tracking-[0.12em] lg:text-8xl" style={{ textShadow: `0 0 40px ${color}` }}>{f.toUpperCase()}</div>
          {ROOT_GLOSS[f] && <div data-testid="reveal-gloss" className="mt-3 text-xl tracking-[0.12em] text-[var(--ink)]" style={{ fontFamily: 'var(--font-mono)' }}>{glossLine(f)}</div>}
          <div className="mt-3 text-sm tracking-[0.4em] text-[var(--muted)]">FEELINGS DECODED {snap.discovered.length}/{snap.feelings.length}</div>
        </div>
      )}
    </div>
  );
};

export const FreeEndScreen = ({ snap }) => (
  <div data-testid="end-screen" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-[#050807]/70 text-center">
    <div className="label-eyebrow">SPORE LAB · [ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · RESEARCH COMPLETE</div>
    <h2 data-testid="end-count" className="font-display mt-6 text-6xl font-black lg:text-9xl">{snap.discovered.length}/{snap.feelings.length} <span className="glow-spore">FEELINGS DECODED</span></h2>
    <div data-testid="end-lab-log" className="mt-8 w-[min(1200px,80vw)] border border-[var(--hair-strong)] p-5 text-left">
      <div className="label-eyebrow mb-3 flex justify-between"><span>ZE MYCOLOGIST&apos;S FIELD DICTIONARY · EXPERIMENT · GUESS</span><span>HIVE ROOTS · SPECTRAL ANALYSIS</span></div>
      <div className={`grid gap-x-10 ${snap.discovered.length > 6 ? 'grid-flow-col grid-cols-2 grid-rows-6' : ''}`}>
        {snap.discovered.map((f) => (
          <div key={f} data-testid={`end-log-${f}`} className="flex justify-between gap-4 border-b border-white/5 py-1.5 text-base">
            <span className="text-[var(--amber)]">EXP {String(snap.feelings.indexOf(f) + 1).padStart(2, '0')} · {snap.guesses[f] || '—'} (?)</span>
            <span className="text-right">
              <span data-testid={`end-gloss-${f}`} className="mr-3 text-xs text-[var(--muted)]">{glossLine(f)}</span>
              <span className="font-bold tracking-[0.2em]" style={{ color: FEELING_COLORS3[f] }}>{f.toUpperCase()}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-8 w-[min(760px,60vw)] text-left"><Transmission tx={snap.ui.transmission} /></div>
  </div>
);

export const FreeHostHud = ({ snap, lab }) => {
  const st = lab.status;
  const b = snap.touch.baseline;
  const vq = snap.voiceQueue;
  const sc = snap.feelingScores || {};
  return (
    <div data-testid="host-hud" className="pointer-events-none absolute right-6 top-36 w-[360px] border border-[var(--hair-strong)] bg-[#050807]/90 p-4 text-xs backdrop-blur-md">
      <div className="mb-2 tracking-[0.2em]">HOST · {st.status.toUpperCase()} {st.deviceName ? `· ${st.deviceName}` : ''}</div>
      {snap.hostHint && <div data-testid="host-prompt" className="mb-2 border border-[var(--amber)] p-2 text-center text-sm text-[var(--amber)] dot-pulse">N TO ADVANCE ({snap.prompted || 'top feeling'})</div>}
      <Row k="Phase" v={`${snap.phase}${snap.paused ? ' (paused)' : ''}`} testId="host-phase" />
      <Row k="Prompted" v={`${snap.prompted || '—'} · L${snap.promptLevel} · repeats ${snap.repeats}`} />
      <Row k="No new feeling" v={`${snap.sinceFoundS.toFixed(0)} s`} testId="host-timer" warn={snap.sinceFoundS > 45} />
      <Row k="Scores" v={['pleasure', 'pain', 'love', 'hate', 'ecstasy'].map((f) => `${f.slice(0, 3)} ${(sc[f] || 0).toFixed(2)}`).join(' ')} />
      <div className="mt-2 text-[var(--muted)]">Signal health</div>
      <Row k="Touch" v={`${snap.touch.touchActive ? 'HUMAN' : 'background'} · ${(snap.touch.touchMs / 1000).toFixed(1)} s · score ${snap.touch.score.toFixed(1)}`} warn={snap.touch.touchMs > 15000} testId="host-touch" />
      <Row k="Baseline age" v={snap.touch.learning ? `LEARNING ${(snap.touch.learnRemainingMs / 1000).toFixed(1)}s` : `${b.ageS.toFixed(0)} s · CC ${b.median.level.toFixed(0)} ± ${b.ccStd.toFixed(1)}`} testId="host-baseline" />
      <Row k="Auto re-baselines" v={b.rebaselines} testId="host-rebaselines" warn={b.rebaselines > 0} />
      <Row k="Touch sensitivity" v={`${snap.sensitivity}/5`} testId="host-strictness" />
      <div className="mt-2 text-[var(--muted)]">Media</div>
      <Row k="Script" v={`${snap.scriptId || 'built-in'}${snap.ui.generating ? ` · generating ${snap.ui.generating.voices}/${snap.ui.generating.total}` : ''}`} />
      <Row k="Voices" v={`${snap.preload.voices}/${snap.preload.voicesAvailable}${snap.preload.voicesFailed ? ` · ${snap.preload.voicesFailed} failed → subtitles` : ''}`} warn={snap.preload.voicesFailed > 0} testId="host-voices" />
      <Row k="Mushroom SFX" v={`${snap.preload.sfx}/${snap.preload.sfxAvailable}`} warn={snap.preload.sfx < snap.preload.sfxAvailable} testId="host-sfx" />
      <Row k="Voice now" v={vq.playing ? `${vq.playing.id} (${snap.ui.transmission ? snap.ui.transmission.voiceSource : vq.playing.mode})` : 'idle'} />
      <Row k="Audio" v={snap.muted ? 'MUTED' : snap.audioReady ? 'on' : 'locked (press Space)'} warn={snap.muted || !snap.audioReady} />
      <Row k="Simulator" v={lab.sim.enabled ? 'ON' : 'off'} warn={lab.sim.enabled} />
    </div>
  );
};

const KEYS = [
  ['Space', 'start / pause'], ['N', 'award prompted (or top) feeling'], ['1–6', 'professor prompts that feeling'], ['H', 'next prompt / commentary now'],
  ['↑ / ↓', 'excitation ±10%'], ['B', 're-learn base state'], ['X', 'save 60 s raw snapshot'], ['C', 'calibrate'], ['[ / ]', 'touch sensitivity'],
  ['M', 'mute'], ['D', 'host HUD'], ['?', 'this sheet'], ['F', 'fullscreen'], ['L', 'signal lab'], ['G / Shift+G', 'new script / repair'], ['R ×2', 'reset'],
  ['S', 'simulator on/off'], ['Q / E / A', 'hold: gentle / rough / many hands'], ['W', 'poke'],
];

export const FreeCheatSheet = () => (
  <div data-testid="cheat-sheet" className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#050807]/80 backdrop-blur-md">
    <div className="grid grid-cols-2 gap-x-12 gap-y-3 border border-[var(--hair-strong)] p-10 text-lg">
      {KEYS.map(([k, d]) => (
        <div key={k} className="flex items-center gap-4"><span className="kbd min-w-[5.5rem] px-2">{k}</span><span className="text-[var(--muted)]">{d}</span></div>
      ))}
    </div>
  </div>
);
